import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";
import {
  buildDiscoveryIframeUrl,
  captureDiscoverySource,
  isQottaDiscoveryMessage,
  type DiscoveryLaunchOptions,
  type DiscoverySource,
} from "@/discovery/presentation";
import { DiscoveryHostContext, type DiscoveryHostApi } from "@/components/discovery-host-context";

type ActiveDiscovery = DiscoveryLaunchOptions & { source: DiscoverySource };

export function DiscoveryHostProvider({ children }: { children: ReactNode }) {
  const [active, setActive] = useState<ActiveDiscovery | null>(null);
  const [onboardingRequest, setOnboardingRequest] = useState<{ returnFocus: HTMLElement | null } | null>(null);
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const dialogRef = useRef<HTMLDivElement>(null);
  const returnFocusRef = useRef<HTMLElement | null>(null);

  const submissionPending = useRef(false);
  const closeDiscovery = useCallback(() => {
    if (!submissionPending.current) setActive(null);
  }, []);
  useEffect(() => {
    const warn = (event: BeforeUnloadEvent) => {
      if (!submissionPending.current) return;
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, []);
  const openDiscovery = useCallback((options: DiscoveryLaunchOptions) => {
    if (submissionPending.current) return;
    const focused = document.activeElement;
    returnFocusRef.current = focused instanceof HTMLElement ? focused : null;
    setActive({ ...options, source: captureDiscoverySource() });
  }, []);

  const iframeSrc = useMemo(
    () => (active ? buildDiscoveryIframeUrl(active, active.source) : null),
    [active],
  );
  const iframeOrigin = useMemo(
    () => (iframeSrc ? new URL(iframeSrc, window.location.href).origin : null),
    [iframeSrc],
  );

  useEffect(() => {
    if (!active) {
      const target = returnFocusRef.current;
      returnFocusRef.current = null;
      if (target?.isConnected) window.requestAnimationFrame(() => target.focus());
      return;
    }

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    dialogRef.current?.focus();

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      event.preventDefault();
      closeDiscovery();
    };
    window.addEventListener("keydown", handleKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [active, closeDiscovery]);

  useEffect(() => {
    if (!active || !iframeOrigin) return;

    const handleMessage = (event: MessageEvent) => {
      if (event.origin !== iframeOrigin) return;
      if (event.source !== iframeRef.current?.contentWindow) return;
      if (!isQottaDiscoveryMessage(event.data)) return;
      if (event.data.type === "qotta:submitting") {
        submissionPending.current = event.data.pending;
        return;
      }
      if (submissionPending.current) return;
      if (event.data.type === "qotta:close") closeDiscovery();
      if (event.data.type === "qotta:signup") {
        closeDiscovery();
        setOnboardingRequest({ returnFocus: returnFocusRef.current });
      }
    };

    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, [active, closeDiscovery, iframeOrigin]);

  const api = useMemo<DiscoveryHostApi>(
    () => ({ openDiscovery, closeDiscovery, isDiscoveryOpen: Boolean(active), onboardingRequest }),
    [active, closeDiscovery, openDiscovery, onboardingRequest],
  );

  return (
    <DiscoveryHostContext.Provider value={api}>
      <div
        data-testid="discovery-host-content"
        aria-hidden={active ? true : undefined}
        inert={active ? true : undefined}
      >
        {children}
      </div>
      {active && iframeSrc
        ? createPortal(
            <div
              className="fixed inset-0 z-[100] grid place-items-center bg-foreground/20 p-0 backdrop-blur-[3px] sm:p-6"
              data-testid="discovery-overlay"
              onMouseDown={(event) => {
                if (event.currentTarget === event.target) closeDiscovery();
              }}
            >
              <div
                ref={dialogRef}
                role="dialog"
                aria-modal="true"
                aria-label="Qotta Discovery"
                tabIndex={-1}
                className="animate-scale-in relative h-[100dvh] w-full overflow-hidden bg-surface shadow-[0_28px_90px_-24px_var(--shadow-color-strong)] outline-none sm:h-[88vh] sm:w-[min(1100px,92vw)] sm:rounded-[28px] sm:border sm:border-border/80"
              >
                <iframe
                  ref={iframeRef}
                  src={iframeSrc}
                  title="Qotta Discovery"
                  data-testid="discovery-iframe"
                  className="h-full w-full border-0 bg-surface"
                  sandbox="allow-forms allow-same-origin allow-scripts"
                  allow="camera 'none'; microphone 'none'; geolocation 'none'; payment 'none'"
                  referrerPolicy="strict-origin-when-cross-origin"
                  onLoad={() => iframeRef.current?.contentWindow?.focus()}
                />
              </div>
            </div>,
            document.body,
          )
        : null}
    </DiscoveryHostContext.Provider>
  );
}
